import {
  FieldControl,
  FieldFooter,
  FieldLabel,
  FieldShell,
  getInputClassName,
  unlabeledAriaProps,
} from "../../field-ui";
import {
  forwardRef,
  useId,
  useImperativeHandle,
  useLayoutEffect,
  useRef,
  useState,
} from "react";
import type { FormFieldInputProps, FormFieldRef } from "../../types";
import { reportFieldValue } from "../../../../lib/forms/reportFieldValue";
import {
  formatNationalPhoneInput,
  formatNationalPhonePart,
  formatPhoneNumber,
  parseStoredPhoneValue,
} from "../../../../lib/forms/phoneFormat";

interface PhoneInputProps extends FormFieldInputProps {
  placeholder: string;
  labelMuted?: boolean;
  className?: string;
  footertext?: string;
  defaultValue?: string;
  validation?: (value: string) => boolean;
}

const PhoneInput = forwardRef<FormFieldRef, PhoneInputProps>((props, ref) => {
  const shellRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const pendingCursorRef = useRef<number | null>(null);
  const inputId = useId();
  const [nationalDigits, setNationalDigits] = useState(() =>
    parseStoredPhoneValue(props.initialValue ?? props.defaultValue ?? ""),
  );
  const [validationState, setValidationState] = useState(true);

  const displayValue = formatNationalPhonePart(nationalDigits);
  const storedValue = nationalDigits ? formatPhoneNumber(nationalDigits) : "";

  const commitNationalDigits = (nextNationalDigits: string) => {
    setNationalDigits(nextNationalDigits);
    reportFieldValue(
      props.fieldName,
      nextNationalDigits ? formatPhoneNumber(nextNationalDigits) : "",
      props.onValueChange,
    );
  };

  useImperativeHandle(ref, () => ({
    setValidationState,
    value: storedValue,
    fieldId: props.fieldId,
    fieldName: props.fieldName,
    scrollToElement: (options) => {
      shellRef.current?.scrollIntoView({
        behavior: "smooth",
        block: "center",
        ...options,
      });
    },
    validate,
    clear,
  }));

  useLayoutEffect(() => {
    if (pendingCursorRef.current == null || !inputRef.current) return;
    inputRef.current.setSelectionRange(
      pendingCursorRef.current,
      pendingCursorRef.current,
    );
    pendingCursorRef.current = null;
  }, [nationalDigits]);

  const clear = () => commitNationalDigits("");

  const validate = () => {
    if (!props.validation) return true;
    const result = props.validation(storedValue);
    setValidationState(result);
    return result;
  };

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setValidationState(true);
    const { value, cursor } = formatNationalPhoneInput(
      event.target.value,
      event.target.selectionStart ?? event.target.value.length,
    );
    pendingCursorRef.current = cursor;
    commitNationalDigits(parseStoredPhoneValue(value));
  };

  return (
    <FieldShell ref={shellRef} className={props.className}>
      <FieldLabel htmlFor={inputId} muted={props.labelMuted}>
        {props.placeholder}
      </FieldLabel>
      <FieldControl className="relative">
        <span
          aria-hidden
          className="pointer-events-none absolute inset-y-0 left-3 z-10 flex items-center font-onest text-[15px] leading-snug text-slate-900"
        >
          +7
        </span>
        <input
          ref={inputRef}
          id={inputId}
          value={displayValue}
          onChange={handleChange}
          onBlur={validate}
          type="tel"
          name={props.fieldName}
          inputMode="numeric"
          aria-invalid={!validationState}
          {...unlabeledAriaProps(props.placeholder, props.accessibleName)}
          autoComplete="tel-national"
          placeholder="(999) 123-45-67"
          className={getInputClassName(
            validationState,
            "form-field-input--prefix pl-[2.65rem]",
          )}
        />
      </FieldControl>
      <FieldFooter>
        {!validationState ? (
          <span className="text-red-600" role="alert">
            Введите 10 цифр номера после +7
          </span>
        ) : props.footertext ? (
          <span className="text-slate-500">{props.footertext}</span>
        ) : null}
      </FieldFooter>
    </FieldShell>
  );
});

PhoneInput.displayName = "PhoneInput";

export { PhoneInput };
export type { PhoneInputProps };

export {
  extractNationalPhoneDigits,
  formatNationalPhoneInput,
  formatNationalPhonePart,
  formatPhoneNumber,
  normalizePhoneDigits,
  parseStoredPhoneValue,
} from "../../../../lib/forms/phoneFormat";
